import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { baseUrl } from "../Univ.js";

const TAB_CONFIG = [
    { id: "patients", label: "Patient Search", icon: "fa-users", color: "#2563eb" },
    { id: "op", label: "OP Patients", icon: "fa-calendar-check", color: "#0d9488" },
    { id: "consultant", label: "Consultant Requests", icon: "fa-user-doctor", color: "#7c3aed" },
    { id: "saved", label: "Saved Requests", icon: "fa-clipboard-check", color: "#16a34a" },
    { id: "ip", label: "IP Requests", icon: "fa-bed", color: "#ea580c" },
    { id: "indent", label: "Indent Requests", icon: "fa-boxes-stacked", color: "#c026d3" },
];

const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.patients)) return data.patients;
    return [];
};

const getLocalDate = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

/*
 * Formats dates and times without fractional seconds.
 *
 * 2026-10-15T13:12:00.434543 -> 2026-10-15 13:12:00
 * 13:12:00.434543             -> 13:12:00
 * 2026-10-15                  -> 2026-10-15
 *
 * Does not convert UTC/local time or invent missing time values.
 */
const formatDateTime = (value) => {
    if (value === null || value === undefined || value === "") return "";

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return "";
        return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")} ${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}:${String(value.getSeconds()).padStart(2, "0")}`;
    }

    return String(value)
        .trim()
        .replace("T", " ")
        .replace(/(\d{2}:\d{2}:\d{2})\.\d+/g, "$1")
        .replace(/(\d{2}:\d{2})\.\d+/g, "$1");
};

const dateCell = (value) => (
    <span title={formatDateTime(value)}>
        {formatDateTime(value) || "—"}
    </span>
);

const dateTimeCell = (date, time) => {
    const formattedDate = formatDateTime(date);
    const formattedTime = formatDateTime(time);

    if (!formattedDate && !formattedTime) return "—";

    // Avoid repeating the time if the date value already contains it.
    const dateHasTime = /\d{2}:\d{2}/.test(formattedDate);
    const timeHasValue = Boolean(formattedTime);

    return (
        <span title={[formattedDate, !dateHasTime ? formattedTime : ""].filter(Boolean).join(" ")}>
            {formattedDate}
            {!dateHasTime && timeHasValue ? ` ${formattedTime}` : ""}
        </span>
    );
};

function DrugView() {
    const [activeTab, setActiveTab] = useState("patients");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedDate, setSelectedDate] = useState(getLocalDate());

    const [patients, setPatients] = useState([]);
    const [opData, setOpData] = useState([]);
    const [consultantData, setConsultantData] = useState([]);
    const [savedData, setSavedData] = useState([]);
    const [ipData, setIpData] = useState([]);
    const [indentData, setIndentData] = useState([]);

    const [loading, setLoading] = useState({});
    const [searchPerformed, setSearchPerformed] = useState(false);

    const setTableLoading = useCallback((key, value) => {
        setLoading((previous) => ({
            ...previous,
            [key]: value,
        }));
    }, []);

    const fetchTable = useCallback(async (key, endpoint, setter) => {
        setTableLoading(key, true);

        try {
            const response = await axios.get(`${baseUrl}${endpoint}`);
            setter(getArray(response.data));
        } catch (error) {
            console.error(`Unable to load ${key}:`, error);
            setter([]);

            Swal.fire({
                icon: "error",
                title: "Unable to load data",
                text: `Could not load ${key.replace(/([A-Z])/g, " $1")}. Please try again.`,
            });
        } finally {
            setTableLoading(key, false);
        }
    }, [setTableLoading]);

    const searchPatients = useCallback(async () => {
        if (!searchTerm.trim()) {
            Swal.fire(
                "Search Patient",
                "Enter patient ID, name, address or mobile number.",
                "info"
            );
            return;
        }

        setSearchPerformed(true);
        setTableLoading("patients", true);

        try {
            const response = await axios.get(
                `${baseUrl}pat-search/?search=${encodeURIComponent(searchTerm.trim())}`
            );
            setPatients(getArray(response.data));
        } catch (error) {
            console.error("Patient search failed:", error);
            setPatients([]);
            Swal.fire("Error", "Unable to search patients.", "error");
        } finally {
            setTableLoading("patients", false);
        }
    }, [searchTerm, setTableLoading]);

    const fetchOPPatients = useCallback(() => {
        fetchTable("op", `op-cons-view/${selectedDate}/`, setOpData);
    }, [fetchTable, selectedDate]);

    const fetchSavedRequests = useCallback(() => {
        fetchTable("saved", `pharm-sale-other/${selectedDate}/`, setSavedData);
    }, [fetchTable, selectedDate]);

    const fetchConsultantRequests = useCallback(() => {
        fetchTable("consultant", `cons-drug-view/${selectedDate}/`, setConsultantData);
    }, [fetchTable, selectedDate]);

    const fetchIPRequests = useCallback(() => {
        fetchTable("ip", `ip-bill-amt-drug/${selectedDate}/`, setIpData);
    }, [fetchTable, selectedDate]);

    const fetchIndentRequests = useCallback(() => {
        fetchTable("indent", `indent-drug-view/${selectedDate}/`, setIndentData);
    }, [fetchTable, selectedDate]);

    const refreshAll = useCallback(() => {
        fetchOPPatients();
        fetchSavedRequests();
        fetchConsultantRequests();
        fetchIPRequests();
        fetchIndentRequests();
    }, [
        fetchOPPatients,
        fetchSavedRequests,
        fetchConsultantRequests,
        fetchIPRequests,
        fetchIndentRequests,
    ]);

    useEffect(() => {
        refreshAll();
    }, [refreshAll]);

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Delete saved request?",
            text: "Are you sure you want to delete this request?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626",
        });

        if (!result.isConfirmed) return;

        try {
            await axios.delete(`${baseUrl}delete-save-pharm/${id}/`);

            setSavedData((previous) =>
                previous.filter((item) => String(item.id) !== String(id))
            );

            await Swal.fire("Deleted", "Saved request has been deleted.", "success");
        } catch (error) {
            console.error("Delete failed:", error);
            Swal.fire("Error", "The request could not be deleted.", "error");
        }
    };

    // ---------------------------------------------------------
    // PATIENT SEARCH COLUMNS
    // ---------------------------------------------------------

    const patientColumns = [
        {
            field: "pat_id",
            headerName: "Patient ID",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Link to={`/drug-pay-other/${params.row.pat_id ?? params.value}`} className="drug-link">
                    {params.row.pat_id ?? params.value}
                </Link>
            ),
        },
        { field: "patient", headerName: "Patient Name", minWidth: 200, flex: 1.3 },
        { field: "gender", headerName: "Gender", width: 100 },
        { field: "age", headerName: "Age", width: 90 },
        { field: "marital_status", headerName: "Marital Status", minWidth: 140 },
        { field: "adhar", headerName: "Aadhaar", minWidth: 140 },
        { field: "address1", headerName: "Address", minWidth: 200, flex: 1 },
        { field: "mobile", headerName: "Mobile", minWidth: 130 },
    ];

    // ---------------------------------------------------------
    // OP PATIENT COLUMNS
    // ---------------------------------------------------------

    const opColumns = [
        { field: "appt_id", headerName: "Appointment ID", minWidth: 160 },
        { field: "pat__pat_id", headerName: "Patient ID", minWidth: 150 },
        { field: "pat__patient", headerName: "Patient Name", minWidth: 200, flex: 1 },
        { field: "pat__mobile", headerName: "Mobile", minWidth: 120 },
        { field: "cons__consultant", headerName: "Consultant", minWidth: 160 },
        { field: "addedby", headerName: "Added By", minWidth: 130 },
        {
            field: "add_date_date",
            headerName: "Appointment Date & Time",
            minWidth: 200,
            renderCell: (params) => dateTimeCell(
                params.row.add_date_date ?? params.row.appt_date ?? params.row.add_date,
                params.row.appt_time
            ),
        },
        {
            field: "appt_time",
            headerName: "Appointment Time",
            minWidth: 140,
            renderCell: (params) => dateCell(params.row.appt_time),
        },
        {
            field: "action",
            headerName: "Action",
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Link
                    to={`/drug-pay-other/${params.row.pat__pat_id}`}
                    className="drug-action drug-action-teal"
                >
                    View
                </Link>
            ),
        },
    ];

    // ---------------------------------------------------------
    // CONSULTANT REQUEST COLUMNS
    // ---------------------------------------------------------

    const consultantColumns = [
        {
            field: "appt_id__appt_id",
            headerName: "Appointment ID",
            minWidth: 160,
        },
        { field: "pat__pat_id", headerName: "Patient ID", minWidth: 150 },
        {
            field: "pat__patient",
            headerName: "Patient Name",
            minWidth: 210,
            flex: 1,
        },
        { field: "addedby", headerName: "Added By", minWidth: 140 },
        {
            field: "appt_id__appt_date",
            headerName: "Appointment Date & Time",
            minWidth: 200,
            renderCell: (params) => dateTimeCell(
                params.row.appt_id__appt_date,
                params.row.appt_id__appt_time
            ),
        },
        {
            field: "action",
            headerName: "Action",
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Link
                    to={`/drug-pay-dr/${params.row.id}`}
                    className="drug-action drug-action-purple"
                >
                    View
                </Link>
            ),
        },
    ];

    // ---------------------------------------------------------
    // SAVED REQUEST COLUMNS
    // ---------------------------------------------------------

    const savedColumns = [
        {
            field: "add_date",
            headerName: "Added Date & Time",
            minWidth: 190,
            renderCell: (params) => dateCell(params.row.add_date),
        },
        { field: "id", headerName: "ID", width: 80 },
        {
            field: "pat_pat_id",
            headerName: "Patient ID",
            minWidth: 140,
            renderCell: (params) => (
                <span>
                    {params.row.pat?.pat_id ?? params.row.pat_pat_id ?? ""}
                </span>
            ),
        },
        { field: "patient", headerName: "Patient Name", minWidth: 200, flex: 1 },
        { field: "addedby", headerName: "Added By", minWidth: 140 },
        {
            field: "view",
            headerName: "View",
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <Link
                    to={`/drug-pay-save/${params.row.id}`}
                    className="drug-action drug-action-green"
                >
                    View
                </Link>
            ),
        },
        {
            field: "delete",
            headerName: "Delete",
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <button
                    type="button"
                    onClick={() => handleDelete(params.row.id)}
                    className="drug-delete-button"
                    title="Delete saved request"
                >
                    <i className="fa-solid fa-trash-can" />
                </button>
            ),
        },
    ];

    // ---------------------------------------------------------
    // IP REQUEST COLUMNS
    // renderCell replaces valueGetter for related fields.
    // ---------------------------------------------------------

    const ipColumns = [
        {
            field: "ip_id",
            headerName: "IP ID",
            minWidth: 150,
            renderCell: (params) => params.row.ip?.ip_id ?? "",
        },
        {
            field: "pat_id",
            headerName: "Patient ID",
            minWidth: 150,
            renderCell: (params) => params.row.pat?.pat_id ?? "",
        },
        {
            field: "patient",
            headerName: "Patient Name",
            minWidth: 210,
            flex: 1,
            renderCell: (params) => params.row.pat?.patient ?? "",
        },
        { field: "addedby", headerName: "Added By", minWidth: 140 },
        {
            field: "add_date",
            headerName: "Raised Date & Time",
            minWidth: 200,
            renderCell: (params) => dateTimeCell(
                params.row.add_date,
                params.row.add_time
            ),
        },
        {
            field: "action",
            headerName: "Action",
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Link
                    to={`/drug-ip-pay/${params.row.id}/`}
                    className="drug-action drug-action-orange"
                >
                    View
                </Link>
            ),
        },
    ];

    // ---------------------------------------------------------
    // INDENT REQUEST COLUMNS
    // ---------------------------------------------------------

    const indentColumns = [
        { field: "dept", headerName: "Department", minWidth: 180 },
        { field: "user", headerName: "Department User", minWidth: 180 },
        { field: "addedby", headerName: "Added By", minWidth: 150 },
        {
            field: "add_date_date",
            headerName: "Added Date & Time",
            minWidth: 200,
               renderCell: (params) => dateTimeCell(
                params.row.add_date,
                params.row.add_time
            ),
        },
        {
            field: "action",
            headerName: "Action",
            width: 110,
            sortable: false,
            renderCell: (params) => (
                <Link
                    to={`/drug-pay-indent/${params.row.id}`}
                    className="drug-action drug-action-pink"
                >
                    View
                </Link>
            ),
        },
    ];

    // ---------------------------------------------------------
    // TABLE CONFIGURATION
    // ---------------------------------------------------------

    const tableDefinitions = {
        patients: {
            title: "Patient Search Results",
            subtitle: "Search and select a patient to manage drug payments.",
            rows: patients,
            columns: patientColumns,
            loading: !!loading.patients,
            color: "#2563eb",
            empty: searchPerformed
                ? "No patients found for this search."
                : "Search for a patient to display results.",
            getRowId: (row) => row.id ?? row.pat_id,
        },
        op: {
            title: "OP Patients",
            subtitle: "Outpatient appointments for the selected date.",
            rows: opData,
            columns: opColumns,
            loading: !!loading.op,
            color: "#0d9488",
            empty: "No OP appointments found for this date.",
            getRowId: (row) => row.id ?? row.appt_id,
        },
        consultant: {
            title: "Consultant Requests",
            subtitle: "Consultant drug requests for the selected date.",
            rows: consultantData,
            columns: consultantColumns,
            loading: !!loading.consultant,
            color: "#7c3aed",
            empty: "No consultant requests found for this date.",
            getRowId: (row) => row.id ?? row.appt_id__appt_id,
        },
        saved: {
            title: "Saved Requests",
            subtitle: "Saved pharmacy requests for the selected date.",
            rows: savedData,
            columns: savedColumns,
            loading: !!loading.saved,
            color: "#16a34a",
            empty: "No saved requests found for this date.",
            getRowId: (row) => row.id,
        },
        ip: {
            title: "IP Requests",
            subtitle: "Inpatient drug requests for the selected date.",
            rows: ipData,
            columns: ipColumns,
            loading: !!loading.ip,
            color: "#ea580c",
            empty: "No inpatient requests found for this date.",
            getRowId: (row) => row.id,
        },
        indent: {
            title: "Indent Requests",
            subtitle: "Department indent requests for the selected date.",
            rows: indentData,
            columns: indentColumns,
            loading: !!loading.indent,
            color: "#c026d3",
            empty: "No indent requests found for this date.",
            getRowId: (row) => row.id,
        },
    };

    const currentTable = tableDefinitions[activeTab];
    const dateBasedTabs = ["op", "consultant", "saved", "ip", "indent"];

    return (
        <section className="drug-view-page">
            <style>{`
                .drug-view-page {
                    --drug-ink: #172033;
                    --drug-muted: #64748b;
                    color: var(--drug-ink);
                    padding: 18px 12px 28px;
                    max-width: 100%;
                }

                .drug-page-heading {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 14px;
                    margin-bottom: 18px;
                }

                .drug-page-title {
                    font-size: 25px;
                    font-weight: 800;
                    margin: 0;
                    letter-spacing: -.4px;
                }

                .drug-page-subtitle {
                    margin: 5px 0 0;
                    color: var(--drug-muted);
                    font-size: 13px;
                }

                .drug-others-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 17px;
                    border-radius: 9px;
                    background: #dc2626;
                    color: #fff;
                    text-decoration: none;
                    font-weight: 700;
                    box-shadow: 0 4px 12px #dc262633;
                }

                .drug-others-link:hover {
                    color: #fff;
                    background: #b91c1c;
                }

                .drug-search-panel {
                    display: flex;
                    align-items: end;
                    gap: 10px;
                    flex-wrap: wrap;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 15px;
                    margin-bottom: 17px;
                }

                .drug-search-field {
                    flex: 1 1 260px;
                }

                .drug-search-field label,
                .drug-date-field label {
                    display: block;
                    font-size: 12px;
                    font-weight: 800;
                    color: #475569;
                    margin-bottom: 6px;
                }

                .drug-search-field input,
                .drug-date-field input {
                    display: block;
                    width: 100%;
                    min-height: 41px;
                    padding: 8px 11px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    background: white;
                    outline: none;
                }

                .drug-search-field input:focus,
                .drug-date-field input:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px #2563eb18;
                }

                .drug-search-button {
                    min-height: 41px;
                    border: 0;
                    border-radius: 8px;
                    background: #2563eb;
                    color: #fff;
                    padding: 9px 18px;
                    font-weight: 700;
                    cursor: pointer;
                }

                .drug-search-button:hover {
                    background: #1d4ed8;
                }

                .drug-search-button:disabled {
                    opacity: .6;
                    cursor: not-allowed;
                }

                .drug-date-field {
                    width: 200px;
                    max-width: 100%;
                }

                .drug-tabs {
                    display: flex;
                    flex-wrap: nowrap;
                    overflow-x: auto;
                    gap: 5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0 2px;
                    margin: 8px 0 0;
                    scrollbar-width: thin;
                }

                .drug-tab {
                    position: relative;
                    flex: 0 0 auto;
                    display: inline-flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    padding: 13px 16px;
                    border: 0;
                    border-radius: 9px 9px 0 0;
                    background: #f1f5f9;
                    color: #475569;
                    font-size: 13px;
                    font-weight: 750;
                    white-space: nowrap;
                    cursor: pointer;
                    transition: background .18s ease, color .18s ease;
                }

                .drug-tab:hover {
                    background: #e2e8f0;
                }

                .drug-tab.active {
                    color: #fff;
                    background: var(--tab-color);
                    box-shadow: 0 -2px 10px #0f172a0d;
                }

                .drug-tab-count {
                    min-width: 22px;
                    height: 22px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 20px;
                    padding: 0 6px;
                    background: #ffffff35;
                    font-size: 11px;
                }

                .drug-tab:not(.active) .drug-tab-count {
                    background: #e2e8f0;
                    color: #334155;
                }

                .drug-table-panel {
                    margin-top: 0;
                    border: 1px solid #e2e8f0;
                    border-top: 4px solid var(--section-color);
                    border-radius: 0 0 13px 13px;
                    overflow: hidden;
                    background: #fff;
                    box-shadow: 0 7px 24px #0f172a0a;
                }

                .drug-table-heading {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 10px;
                    padding: 17px 18px;
                    background: var(--section-pale);
                    border-bottom: 1px solid var(--section-border);
                }

                .drug-table-title {
                    font-size: 19px;
                    font-weight: 800;
                    color: var(--section-color);
                    margin: 0;
                }

                .drug-table-description {
                    margin: 4px 0 0;
                    color: #64748b;
                    font-size: 12px;
                }

                .drug-record-count {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: #fff;
                    color: var(--section-color);
                    border: 1px solid var(--section-border);
                    border-radius: 20px;
                    padding: 7px 12px;
                    font-size: 12px;
                    font-weight: 800;
                }

                .drug-grid-wrap {
                    width: 100%;
                    padding: 12px;
                }

                .drug-link {
                    color: #1d4ed8;
                    text-decoration: none;
                    font-weight: 750;
                }

                .drug-link:hover {
                    text-decoration: underline;
                }

                .drug-action {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 61px;
                    padding: 6px 12px;
                    border-radius: 6px;
                    color: #fff;
                    text-decoration: none;
                    font-size: 12px;
                    font-weight: 750;
                }

                .drug-action:hover {
                    color: #fff;
                    filter: brightness(.9);
                }

                .drug-action-teal { background: #0d9488; }
                .drug-action-purple { background: #7c3aed; }
                .drug-action-green { background: #16a34a; }
                .drug-action-orange { background: #ea580c; }
                .drug-action-pink { background: #c026d3; }

                .drug-delete-button {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 32px;
                    height: 32px;
                    border: 0;
                    border-radius: 7px;
                    background: #fee2e2;
                    color: #dc2626;
                    cursor: pointer;
                }

                .drug-delete-button:hover {
                    background: #dc2626;
                    color: white;
                }

                .drug-grid .MuiDataGrid-columnHeaders {
                    background: var(--section-color);
                    color: #fff;
                    border-radius: 0;
                }

                .drug-grid .MuiDataGrid-columnHeaderTitle {
                    font-weight: 800;
                    font-size: 12px;
                }

                .drug-grid .MuiDataGrid-columnSeparator {
                    color: #ffffff70;
                }

                .drug-grid .MuiDataGrid-cell {
                    border-bottom: 1px solid var(--section-border);
                    font-size: 12px;
                }

                .drug-grid .MuiDataGrid-row:nth-of-type(even) {
                    background: var(--section-pale);
                }

                .drug-grid .MuiDataGrid-row:hover {
                    background: var(--section-hover);
                }

                .drug-grid .MuiDataGrid-footerContainer {
                    background: var(--section-pale);
                    border-top: 1px solid var(--section-border);
                }

                .drug-empty-state {
                    text-align: center;
                    padding: 45px 16px;
                    color: #64748b;
                }

                .drug-empty-state i {
                    display: block;
                    margin-bottom: 12px;
                    font-size: 30px;
                    color: var(--section-color);
                }

                @media (max-width: 700px) {
                    .drug-view-page {
                        padding: 12px 4px 20px;
                    }

                    .drug-page-title {
                        font-size: 21px;
                    }

                    .drug-tab {
                        padding: 11px 12px;
                        font-size: 12px;
                    }

                    .drug-table-heading {
                        padding: 13px;
                    }

                    .drug-table-title {
                        font-size: 16px;
                    }

                    .drug-grid-wrap {
                        padding: 5px;
                    }

                    .drug-date-field {
                        width: 100%;
                    }
                }
            `}</style>

            <div className="drug-page-heading">
                <div>
                    <h2 className="drug-page-title">
                        <i
                            className="fa-solid fa-prescription-bottle-medical me-2"
                            style={{ color: "#2563eb" }}
                        />
                        Drug Management
                    </h2>
                    <p className="drug-page-subtitle">
                        Patient prescriptions, consultant requests and pharmacy indents
                    </p>
                </div>

                <Link to="/drug-pay-other/0" className="drug-others-link">
                    <i className="fa-solid fa-prescription-bottle" />
                    Others
                </Link>
            </div>

            {activeTab === "patients" && (
                <div className="drug-search-panel">
                    <div className="drug-search-field">
                        <label htmlFor="drug-patient-search">Search Patient</label>
                        <input
                            id="drug-patient-search"
                            type="text"
                            placeholder="Patient ID, name, address or mobile"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") searchPatients();
                            }}
                        />
                    </div>

                    <button
                        type="button"
                        className="drug-search-button"
                        onClick={searchPatients}
                        disabled={!!loading.patients}
                    >
                        <i className="fa-solid fa-magnifying-glass me-2" />
                        {loading.patients ? "Searching..." : "Search"}
                    </button>
                </div>
            )}

            {dateBasedTabs.includes(activeTab) && (
                <div className="drug-search-panel">
                    <div className="drug-date-field">
                        <label htmlFor="drug-selected-date">
                            Select {activeTab === "op" ? "Appointment" : "Request"} Date
                        </label>
                        <input
                            id="drug-selected-date"
                            type="date"
                            value={selectedDate}
                            max={getLocalDate()}
                            onChange={(event) => setSelectedDate(event.target.value)}
                        />
                    </div>

                    <button
                        type="button"
                        className="drug-search-button"
                        onClick={refreshAll}
                        disabled={
                            !!loading.op ||
                            !!loading.saved ||
                            !!loading.consultant ||
                            !!loading.ip ||
                            !!loading.indent
                        }
                    >
                        <i className="fa-solid fa-rotate me-2" />
                        Refresh
                    </button>

                    <span className="text-muted small">
                        Showing requests for <strong>{selectedDate}</strong>.
                        Change the date to view another day.
                    </span>
                </div>
            )}

            <nav className="drug-tabs" role="tablist" aria-label="Drug management sections">
                {TAB_CONFIG.map((tab) => {
                    const selected = activeTab === tab.id;
                    const count = tableDefinitions[tab.id].rows.length;

                    return (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            id={`drug-tab-${tab.id}`}
                            aria-selected={selected}
                            aria-controls={`drug-panel-${tab.id}`}
                            className={`drug-tab ${selected ? "active" : ""}`}
                            style={{ "--tab-color": tab.color }}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <i className={`fa-solid ${tab.icon}`} />
                            {tab.label}
                            <span className="drug-tab-count">{count}</span>
                        </button>
                    );
                })}
            </nav>

            <section
                id={`drug-panel-${activeTab}`}
                role="tabpanel"
                aria-labelledby={`drug-tab-${activeTab}`}
                className="drug-table-panel"
                style={{
                    "--section-color": currentTable.color,
                    "--section-pale": `${currentTable.color}0D`,
                    "--section-border": `${currentTable.color}30`,
                    "--section-hover": `${currentTable.color}18`,
                }}
            >
                <div className="drug-table-heading">
                    <div>
                        <h3 className="drug-table-title">
                            <i
                                className={`fa-solid ${TAB_CONFIG.find((tab) => tab.id === activeTab)?.icon} me-2`}
                            />
                            {currentTable.title}
                        </h3>

                        <p className="drug-table-description">
                            {currentTable.subtitle}
                            {dateBasedTabs.includes(activeTab) && (
                                <> Date: {selectedDate}</>
                            )}
                        </p>
                    </div>

                    <div className="drug-record-count">
                        <i className="fa-solid fa-table-list" />
                        {currentTable.rows.length} Records
                    </div>
                </div>

                <div className="drug-grid-wrap">
                    {currentTable.rows.length > 0 || currentTable.loading ? (
                        <DataGrid
                            className="drug-grid"
                            rows={currentTable.rows}
                            columns={currentTable.columns}
                            getRowId={currentTable.getRowId}
                            loading={currentTable.loading}
                            autoHeight
                            disableRowSelectionOnClick
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        page: 0,
                                        pageSize: 10,
                                    },
                                },
                            }}
                            slots={{ toolbar: GridToolbar }}
                            showToolbar
                            sx={{
                                "--section-color": currentTable.color,
                                "--section-pale": `${currentTable.color}0D`,
                                "--section-border": `${currentTable.color}30`,
                                "--section-hover": `${currentTable.color}18`,
                                border: 1,
                                borderColor: `${currentTable.color}40`,
                                borderRadius: 2,
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                                    outline: "none",
                                },
                            }}
                        />
                    ) : (
                        <div className="drug-empty-state">
                            <i
                                className={`fa-solid ${TAB_CONFIG.find((tab) => tab.id === activeTab)?.icon}`}
                            />
                            <strong>{currentTable.empty}</strong>

                            {activeTab === "patients" && !searchPerformed && (
                                <p className="small mt-2 mb-0">
                                    Enter a search term above to find a patient.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </section>
    );
}

export default DrugView;